import React, { useEffect, useState } from 'react';
import {
    Card,
    Container,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useFormik, FormikProvider, Form } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { When } from 'react-if';
import dayjs from 'dayjs';

import { Page } from '../../../../components';
import HeaderBreadcrumbs from '../../../../components/header-breadcrumbs';
import MultiSelect from '../../../../components/multi-select';
import Loading from '../../../../components/loading';
import { RootState } from '../../../../store';
import { PomBannerService } from '../../../../services/pomBanner.service';
import { CustomerService } from '../../../../services/customer.service';
import { PomBannerTypes } from '../../../../types/pomBanner.types';
import { bannerFormSchema } from '../../../../schemas/pomBanner.schema';

const BANNER_TYPES: { label: string; value: PomBannerTypes.BannerType }[] = [
    { label: 'Info', value: 'info' },
    { label: 'Success', value: 'success' },
    { label: 'Warning', value: 'warning' },
    { label: 'Error', value: 'error' },
];

/**
 * Convert an ISO date string to the local datetime-local input format (YYYY-MM-DDTHH:mm).
 */
const toDateTimeLocal = (iso: string | null): string => {
    if (!iso) return '';
    return dayjs(iso).format('YYYY-MM-DDTHH:mm');
};

export const EditBannerScreen: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { pagination, loading } = useSelector((state: RootState) => state.bannersAdmin);
    const [subscriptionOptions, setSubscriptionOptions] = useState<
        { value: string; label: string }[]
    >([]);
    const [banner, setBanner] = useState<PomBannerTypes.Model | null>(null);
    const [initialLoading, setInitialLoading] = useState(true);

    useEffect(() => {
        // Try to find banner from store, otherwise fetch
        const found = pagination.items?.find((b) => b.id === id);
        if (found) {
            setBanner(found);
            setInitialLoading(false);
        } else {
            // Fetch the list if not in store yet
            PomBannerService.fetchMany({ page: 1, perPage: 100 }).then((result) => {
                const match = result?.items?.find((b: PomBannerTypes.Model) => b.id === id);
                if (match) setBanner(match);
                setInitialLoading(false);
            });
        }

        // Fetch customers for subscription selector
        CustomerService.fetchMany({ page: 1, perPage: 500 }).then((result) => {
            if (result?.items) {
                const options = result.items
                    .filter((c: any) => c.subscription?.id)
                    .map((c: any) => ({
                        value: c.subscription.id,
                        label: `${c.name} (${c.subscription.id})`,
                    }));
                setSubscriptionOptions(options);
            }
        }).catch(() => {});
    }, [id]);

    const formik = useFormik<PomBannerTypes.CreateParams & { isActive: boolean }>({
        enableReinitialize: true,
        initialValues: banner
            ? {
                  title: banner.title,
                  message: banner.message,
                  type: banner.type,
                  linkText: banner.linkText || '',
                  linkUrl: banner.linkUrl || '',
                  priority: banner.priority,
                  allowDontShowAgain: banner.allowDontShowAgain,
                  targetAllOrgs: banner.targetAllOrgs,
                  targetSubscriptionIds: banner.targetSubscriptionIds || [],
                  startsAt: toDateTimeLocal(banner.startsAt),
                  expiresAt: toDateTimeLocal(banner.expiresAt),
                  isActive: banner.isActive,
              }
            : {
                  title: '',
                  message: '',
                  type: 'info' as PomBannerTypes.BannerType,
                  linkText: '',
                  linkUrl: '',
                  priority: 0,
                  allowDontShowAgain: true,
                  targetAllOrgs: true,
                  targetSubscriptionIds: [],
                  startsAt: null,
                  expiresAt: null,
                  isActive: true,
              },
        validationSchema: bannerFormSchema,
        onSubmit: async (values) => {
            if (!id) return;

            const payload: PomBannerTypes.UpdateParams = {
                title: values.title,
                message: values.message,
                type: values.type,
                linkText: values.linkText || null,
                linkUrl: values.linkUrl || null,
                priority: values.priority,
                allowDontShowAgain: values.allowDontShowAgain,
                targetAllOrgs: values.targetAllOrgs,
                targetSubscriptionIds: values.targetAllOrgs ? [] : values.targetSubscriptionIds,
                startsAt: values.startsAt ? new Date(values.startsAt).toISOString() : null,
                expiresAt: values.expiresAt ? new Date(values.expiresAt).toISOString() : null,
                isActive: values.isActive,
            };

            await PomBannerService.update(id, payload, (isValid, msg) => {
                toast(msg, { autoClose: 1500, type: isValid ? 'success' : 'error' });
                if (isValid) {
                    navigate('/banners');
                }
            });
        },
    });

    const {
        errors,
        touched,
        values,
        handleSubmit,
        getFieldProps,
        setFieldValue,
    } = formik;

    if (initialLoading) {
        return (
            <Page title="POM - Edit Banner">
                <Container maxWidth={false}>
                    <Loading />
                </Container>
            </Page>
        );
    }

    if (!banner) {
        return (
            <Page title="POM - Edit Banner">
                <Container maxWidth={false}>
                    <Typography>Banner not found.</Typography>
                </Container>
            </Page>
        );
    }

    return (
        <Page title="POM - Edit Banner">
            <Container maxWidth={false}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <HeaderBreadcrumbs
                        heading="Edit Banner"
                        links={[
                            { name: 'Banners', href: '/banners' },
                            { name: 'Edit', href: `/banners/${id}/edit` },
                        ]}
                        activeLast={false}
                    />
                </Stack>

                <Card sx={{ mt: 2, p: 3 }}>
                    <FormikProvider value={formik}>
                        <Form noValidate onSubmit={handleSubmit}>
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                Banner Details
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Title"
                                        placeholder="Admin-facing label for this banner"
                                        error={Boolean(touched.title && errors.title)}
                                        helperText={touched.title && errors.title}
                                        {...getFieldProps('title')}
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth error={Boolean(touched.type && errors.type)}>
                                        <InputLabel>Type</InputLabel>
                                        <Select
                                            label="Type"
                                            {...getFieldProps('type')}
                                        >
                                            {BANNER_TYPES.map((opt) => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {touched.type && errors.type && (
                                            <FormHelperText>{errors.type}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Priority"
                                        placeholder="Lower number = shown first"
                                        error={Boolean(touched.priority && errors.priority)}
                                        helperText={
                                            touched.priority && errors.priority
                                                ? errors.priority
                                                : 'Lower number = higher priority'
                                        }
                                        {...getFieldProps('priority')}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setFieldValue('priority', val === '' ? '' : parseInt(val, 10));
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="Message"
                                        placeholder="Plain text displayed to users in the banner"
                                        error={Boolean(touched.message && errors.message)}
                                        helperText={touched.message && errors.message}
                                        {...getFieldProps('message')}
                                    />
                                </Grid>
                            </Grid>

                            <Typography variant="subtitle1" sx={{ mt: 3, mb: 2 }}>
                                Link (Optional)
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Link Text"
                                        placeholder="e.g. Learn More, View"
                                        error={Boolean(touched.linkText && errors.linkText)}
                                        helperText={
                                            touched.linkText && errors.linkText
                                                ? errors.linkText
                                                : 'Appended to the end of the banner as a clickable link'
                                        }
                                        {...getFieldProps('linkText')}
                                    />
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <TextField
                                        fullWidth
                                        label="Link URL"
                                        placeholder="https://pomsafe.zendesk.com/hc/en-us/articles/..."
                                        error={Boolean(touched.linkUrl && errors.linkUrl)}
                                        helperText={
                                            touched.linkUrl && errors.linkUrl
                                                ? errors.linkUrl
                                                : 'Where the link opens (e.g. a Zendesk article)'
                                        }
                                        {...getFieldProps('linkUrl')}
                                    />
                                </Grid>
                            </Grid>

                            <Typography variant="subtitle1" sx={{ mt: 3, mb: 2 }}>
                                Scheduling
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        type="datetime-local"
                                        label="Starts At"
                                        InputLabelProps={{ shrink: true }}
                                        helperText="Leave empty for immediately active"
                                        value={values.startsAt || ''}
                                        onChange={(e) =>
                                            setFieldValue(
                                                'startsAt',
                                                e.target.value || null,
                                            )
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        type="datetime-local"
                                        label="Expires At"
                                        InputLabelProps={{ shrink: true }}
                                        helperText="Leave empty for no auto-expiry"
                                        value={values.expiresAt || ''}
                                        onChange={(e) =>
                                            setFieldValue(
                                                'expiresAt',
                                                e.target.value || null,
                                            )
                                        }
                                    />
                                </Grid>
                            </Grid>

                            <Typography variant="subtitle1" sx={{ mt: 3, mb: 2 }}>
                                Options
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={values.isActive}
                                                onChange={(e) =>
                                                    setFieldValue('isActive', e.target.checked)
                                                }
                                            />
                                        }
                                        label="Active"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={values.allowDontShowAgain}
                                                onChange={(e) =>
                                                    setFieldValue(
                                                        'allowDontShowAgain',
                                                        e.target.checked,
                                                    )
                                                }
                                            />
                                        }
                                        label="Allow 'Don't Show Again'"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={values.targetAllOrgs}
                                                onChange={(e) =>
                                                    setFieldValue(
                                                        'targetAllOrgs',
                                                        e.target.checked,
                                                    )
                                                }
                                            />
                                        }
                                        label="Target All Organizations"
                                    />
                                </Grid>
                            </Grid>

                            <When condition={!values.targetAllOrgs}>
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                    <Grid item xs={12}>
                                        <MultiSelect
                                            label="Target Subscriptions"
                                            name="targetSubscriptionIds"
                                            placeholder="Select subscriptions..."
                                            values={values.targetSubscriptionIds}
                                            error={Boolean(
                                                touched.targetSubscriptionIds &&
                                                    errors.targetSubscriptionIds,
                                            )}
                                            errorText={
                                                (touched.targetSubscriptionIds &&
                                                    (errors.targetSubscriptionIds as string)) ||
                                                ''
                                            }
                                            options={subscriptionOptions}
                                            setFieldValue={setFieldValue}
                                            emptyText="No subscriptions available"
                                        />
                                    </Grid>
                                </Grid>
                            </When>

                            <Grid container sx={{ mt: 3 }}>
                                <LoadingButton
                                    variant="contained"
                                    loading={loading.update}
                                    type="submit"
                                >
                                    Save Changes
                                </LoadingButton>
                            </Grid>
                        </Form>
                    </FormikProvider>
                </Card>
            </Container>
        </Page>
    );
};
